import { Element } from "@xmpp/xml"
import {
  CrewMemberReading,
  LegalCounters,
  Tacho,
  TachoReading,
} from "@/reducers/tachosSlice"
import {
  asObject,
  asString,
  legalCountersFromJson,
  parseJson,
  parseLegalCounters,
} from "./legalCountersParser"
import { parseWarsawTime } from "./warsawTime"

type ReadAttribute = (name: string) => unknown

const count = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.trunc(value) : null
  }
  if (typeof value !== "string" || value === "") return null
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) ? null : parsed
}

const flag = (value: unknown): boolean => value === true || value === "true"

const instant = (value: unknown): number | null =>
  typeof value === "string" ? parseWarsawTime(value) : null

const counters = (value: unknown): LegalCounters | null =>
  typeof value === "string"
    ? parseLegalCounters(value)
    : legalCountersFromJson(value)

const readTacho = (read: ReadAttribute): TachoReading => ({
  remainingSecondsToday: count(read("remaining_seconds_today")) ?? 0,
  usedSecondsToday: count(read("used_seconds_today")) ?? 0,
  remainingMiddayBreakSeconds: count(read("remaining_midday_break_seconds")),
  remainingBeforeDayStartBreakSeconds: count(
    read("remaining_before_day_start_break_seconds"),
  ),
  remainingAfterDayEndBreakSeconds: count(
    read("remaining_after_day_end_break_seconds"),
  ),
  remainingSecondsThisWeek: count(read("remaining_seconds_this_week")),
  remainingSecondsOfUninterruptedDriving: count(
    read("remaining_seconds_of_uninterrupted_driving"),
  ),
  remainingWeeklyRestSeconds: count(read("remaining_weekly_rest_seconds")),
  remainingShortenedDailyRests: count(read("remaining_shortened_daily_rests")),
  remainingSecondsTwoWeeks: count(read("remaining_seconds_two_weeks")),
  remainingDailyExtensions: count(read("remaining_daily_extensions")),
  usedSecondsThisWeek: count(read("used_seconds_this_week")),
  remainingSecondsNextWeek: count(read("remaining_seconds_next_week")),
  remainingDaysOfDriving: count(read("remaining_days_of_driving")),
  weeklyRestRequiredSeconds: count(read("weekly_rest_required_seconds")),
  weeklyRestInProgress: flag(read("weekly_rest_in_progress")),
  currentActivity: asString(read("current_activity")),
  publishedAt: instant(read("timestamp")),
  dayEndAt: instant(read("day_end_timestamp")),
  weeklyRestDueAt: instant(read("weekly_rest_due_at")),
  shiftStartAt: instant(read("shift_start_time")),
  restStartedAt: instant(read("rest_started_at")),
  doubleCrew: flag(read("double_crew")),
  driverName: asString(read("driver_name")),
  legalCounters: counters(read("legal_counters")),
})

const readCrew = (raw: string | undefined): CrewMemberReading[] | undefined => {
  const entries = parseJson(raw)
  if (!Array.isArray(entries)) return undefined
  const members = entries.flatMap((value) => {
    const entry = asObject(value)
    if (!entry) return []
    const member = readTacho((name) => entry[name])
    return [{ ...member, driverId: asString(entry.driver_id) }]
  })
  return members.length > 0 ? members : undefined
}

export const readingFromElement = (
  element: Element,
): Pick<Tacho, "reading" | "crewReadings"> => {
  const crewReadings = readCrew(element.getAttr("crew"))
  return {
    reading: readTacho((name) => element.getAttr(name)),
    ...(crewReadings ? { crewReadings } : {}),
  }
}
